import { useMemo, useState, type ReactNode } from 'react';
import {
  GridIcon,
  IconSizes,
  ListIcon,
  RowsIcon,
  SearchMdIcon,
} from '@newcore/icons';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Sizes } from '@newcore/ui/enums/sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Input } from '@newcore/ui/inputs';
import {
  SegmentedControl,
  type SegmentedControlOption,
} from '@newcore/ui/segmented-control';
import { Separator } from '@newcore/ui/separator';
import { Text } from '@newcore/ui/typography';
import { EmptyState } from '@/components/common/empty-state';
import { ErrorAlertWithRetry } from '@/components/common/error-alert-with-retry';
import { PageHeader } from '@/components/common/page-header';
import { FilterTabs, type FilterTabOption } from '@/components/ui/filter-tabs';
import { ApplicationRowSkeleton } from '@/components/ui/skeletons/application-row-skeleton';
import { ApplicationTileSkeleton } from '@/components/ui/skeletons/application-tile-skeleton';
import { AppViewMode } from '@/enums/app-view-mode.enums';
import { useRecentApps } from '@/hooks/use-recent-apps';
import { useApplications } from '@/hooks/use-user-portal.queries';
import { useAppViewModeStore } from '@/stores/app-view-mode.store';
import type { UserApplication } from '@/types/api.types';
import { ApplicationRow } from './application-row';
import { ApplicationTile } from './application-tile';

type ApplicationFilter = 'all' | 'favorites';

const RECENT_HEADING = 'Recently used';
const APP_TILE_SKELETON_COUNT = 14;
const APP_ROW_SKELETON_COUNT = 8;

const VIEW_MODE_OPTIONS: SegmentedControlOption<AppViewMode>[] = [
  { value: AppViewMode.ICONS, label: 'Icons', icon: GridIcon },
  { value: AppViewMode.CARDS, label: 'Cards', icon: RowsIcon },
  { value: AppViewMode.LIST, label: 'List', icon: ListIcon },
];

const COLLECTION_CLASSES: Record<AppViewMode, string> = {
  [AppViewMode.ICONS]: 'flex flex-wrap gap-x-12 gap-y-6',
  [AppViewMode.CARDS]: 'grid w-full grid-cols-1 gap-3 sm:grid-cols-2',
  [AppViewMode.LIST]: 'grid w-full grid-cols-1 gap-1',
};
const NO_FAVORITES_MESSAGE = 'No favorite applications';
const NO_APPLICATIONS_MESSAGE = 'No applications available';
const EMPTY_APPLICATIONS: UserApplication[] = [];

function emptyCatalogMessage(
  filter: ApplicationFilter,
  query: string,
  hasApplicationSearchQuery: boolean
): string {
  if (hasApplicationSearchQuery) {
    return `Nothing matches “${query.trim()}”.`;
  }

  if (filter === 'favorites') {
    return NO_FAVORITES_MESSAGE;
  }

  return NO_APPLICATIONS_MESSAGE;
}

interface AllAppsSectionProps {
  onApplicationClick: (application: UserApplication) => void;
  loadingAppId?: string | null;
}

export const AllAppsSection = ({
  onApplicationClick,
  loadingAppId,
}: AllAppsSectionProps) => {
  const {
    data: applications,
    isLoading,
    isError,
    error,
    refetch,
  } = useApplications();
  const { recentAppIds } = useRecentApps();
  const viewMode = useAppViewModeStore(state => state.viewMode);
  const setViewMode = useAppViewModeStore(state => state.setViewMode);

  const [filter, setFilter] = useState<ApplicationFilter>('all');
  const [query, setQuery] = useState('');

  const allApps = applications?.items ?? EMPTY_APPLICATIONS;
  const totalCount = applications?.total ?? allApps.length;
  const isTruncated = allApps.length < totalCount;

  const recentApps = useMemo(() => {
    const byId = new Map(allApps.map(app => [app.app_id, app]));

    return recentAppIds
      .map(id => byId.get(id))
      .filter((app): app is UserApplication => Boolean(app));
  }, [allApps, recentAppIds]);

  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();

    return allApps.filter(app => {
      if (filter === 'favorites' && !app.is_favorite) {
        return false;
      }

      if (!term) {
        return true;
      }

      return app.app_name.toLowerCase().includes(term);
    });
  }, [allApps, filter, query]);

  const hasApplicationSearchQuery = query.trim().length > 0;

  const tabs: FilterTabOption<ApplicationFilter>[] = [
    { value: 'all', label: 'All', count: totalCount },
    {
      value: 'favorites',
      label: 'Favorites',
      count: allApps.filter(app => app.is_favorite).length,
    },
  ];

  const showRecent =
    viewMode === AppViewMode.ICONS &&
    filter === 'all' &&
    !hasApplicationSearchQuery &&
    recentApps.length > 0;

  return (
    <div className='flex w-full flex-col gap-12'>
      <PageHeader
        title='Applications'
        description='Everything you have access to. Apps open in a new tab.'
      />

      <div className='flex w-full flex-wrap items-center gap-4 lg:gap-12'>
        <FilterTabs
          className='flex-1'
          options={tabs}
          value={filter}
          onChange={setFilter}
          areCountsLoading={isLoading}
          aria-label='Filter applications'
        />
        <div className='flex w-full items-center gap-2 sm:w-auto'>
          <Input
            type='search'
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder='Search apps'
            aria-label='Search apps'
            className='w-full sm:w-59'
            leading={
              <SearchMdIcon size={IconSizes.SM} className='text-quaternary' />
            }
          />
          <SegmentedControl
            options={VIEW_MODE_OPTIONS}
            value={viewMode}
            onChange={setViewMode}
            size={Sizes.MD}
            aria-label='View'
            className='shrink-0'
          />
        </div>
      </div>

      {isError && error && (
        <ErrorAlertWithRetry
          message={`Failed to load applications: ${error.message}`}
          onRetry={() => refetch()}
        />
      )}

      {isTruncated && !hasApplicationSearchQuery && (
        <Text size={TextSizes.SM} className='text-quaternary'>
          Showing {allApps.length} of {totalCount} applications. Search may not
          find the rest.
        </Text>
      )}

      {!isError && (
        <CatalogBody
          viewMode={viewMode}
          isLoading={isLoading}
          filter={filter}
          query={query}
          hasApplicationSearchQuery={hasApplicationSearchQuery}
          matches={matches}
          recentApps={recentApps}
          recentAppIds={recentAppIds}
          showRecent={showRecent}
          onApplicationClick={onApplicationClick}
          loadingAppId={loadingAppId}
        />
      )}
    </div>
  );
};

interface CatalogBodyProps {
  viewMode: AppViewMode;
  isLoading: boolean;
  filter: ApplicationFilter;
  query: string;
  hasApplicationSearchQuery: boolean;
  matches: UserApplication[];
  recentApps: UserApplication[];
  recentAppIds: string[];
  showRecent: boolean;
  onApplicationClick: (application: UserApplication) => void;
  loadingAppId?: string | null;
}

function CatalogBody({
  viewMode,
  isLoading,
  filter,
  query,
  hasApplicationSearchQuery,
  matches,
  recentApps,
  recentAppIds,
  showRecent,
  onApplicationClick,
  loadingAppId,
}: CatalogBodyProps) {
  if (isLoading) {
    const showRecentSkeleton =
      viewMode === AppViewMode.ICONS && recentAppIds.length > 0;

    return (
      <div className='flex w-full flex-col gap-6'>
        {showRecentSkeleton ? (
          <section className='flex w-full flex-col gap-3'>
            <SectionHeading>{RECENT_HEADING}</SectionHeading>
            <AppSkeletonCollection viewMode={viewMode} />
          </section>
        ) : (
          <AppSkeletonCollection viewMode={viewMode} />
        )}
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <EmptyState
        message={emptyCatalogMessage(filter, query, hasApplicationSearchQuery)}
      />
    );
  }

  if (hasApplicationSearchQuery) {
    return (
      <section className='flex w-full flex-col gap-3'>
        <SectionHeading>
          {matches.length} {matches.length === 1 ? 'result' : 'results'}
        </SectionHeading>
        <AppCollection
          apps={matches}
          viewMode={viewMode}
          onLaunch={onApplicationClick}
          loadingAppId={loadingAppId}
        />
      </section>
    );
  }

  return (
    <div className='flex w-full flex-col gap-6'>
      {showRecent && (
        <>
          <section className='flex w-full flex-col gap-3'>
            <SectionHeading>{RECENT_HEADING}</SectionHeading>
            <AppCollection
              apps={recentApps}
              viewMode={AppViewMode.ICONS}
              onLaunch={onApplicationClick}
              loadingAppId={loadingAppId}
            />
          </section>
          <Separator />
        </>
      )}

      <AppCollection
        apps={matches}
        viewMode={viewMode}
        onLaunch={onApplicationClick}
        loadingAppId={loadingAppId}
      />
    </div>
  );
}

function AppSkeletonCollection({ viewMode }: { viewMode: AppViewMode }) {
  if (viewMode === AppViewMode.ICONS) {
    return (
      <div className={COLLECTION_CLASSES[viewMode]}>
        {Array.from({ length: APP_TILE_SKELETON_COUNT }, (_, index) => (
          <ApplicationTileSkeleton key={index} />
        ))}
      </div>
    );
  }

  return (
    <div className={COLLECTION_CLASSES[viewMode]}>
      {Array.from({ length: APP_ROW_SKELETON_COUNT }, (_, index) => (
        <ApplicationRowSkeleton key={index} layout={viewMode} />
      ))}
    </div>
  );
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <Text as='h2' weight={FontWeights.MEDIUM} className='text-quaternary'>
      {children}
    </Text>
  );
}

interface AppCollectionProps {
  apps: UserApplication[];
  viewMode: AppViewMode;
  onLaunch: (application: UserApplication) => void;
  loadingAppId?: string | null;
}

function AppCollection({
  apps,
  viewMode,
  onLaunch,
  loadingAppId,
}: AppCollectionProps) {
  if (viewMode === AppViewMode.ICONS) {
    return (
      <div className={COLLECTION_CLASSES[viewMode]}>
        {apps.map(app => (
          <ApplicationTile
            key={app.app_id}
            application={app}
            onClick={onLaunch}
            isLoading={loadingAppId === app.app_id}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={COLLECTION_CLASSES[viewMode]}>
      {apps.map(app => (
        <ApplicationRow
          key={app.app_id}
          application={app}
          layout={viewMode}
          onClick={onLaunch}
          isLoading={loadingAppId === app.app_id}
        />
      ))}
    </div>
  );
}
